/**
 * Hook to check if the current user is a global admin.
 * Uses React Query for caching to prevent unnecessary API calls.
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

interface GlobalAdminStatusData {
  is_global_admin: boolean;
  user_id: string | null;
  email: string | null;
  name: string | null;
}

interface GlobalAdminStatus {
  isGlobalAdmin: boolean;
  userId: string | null;
  email: string | null;
  name: string | null;
  isLoading: boolean;
  error: Error | null;
}

export function useGlobalAdminStatus(): GlobalAdminStatus {
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery<GlobalAdminStatusData>({
    queryKey: ['global-admin-status', user?.id],
    queryFn: async () => {
      const apiClient = await authenticatedBitswanBackendInstance();
      const response = await apiClient.get('/global-admin/status/');
      return response.data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });

  if (!user) {
    return {
      isGlobalAdmin: false,
      userId: null,
      email: null,
      name: null,
      isLoading: false,
      error: null,
    };
  }

  return {
    isGlobalAdmin: data?.is_global_admin === true,
    userId: data?.user_id ?? null,
    email: data?.email ?? null,
    name: data?.name ?? null,
    isLoading,
    error: error as Error | null,
  };
}
